/**
 * Modal Component
 *
 * Reusable modal dialog with consistent styling and behavior
 * - Supports different sizes
 * - Click outside to close
 * - ESC key to close
 * - Optional header/footer
 *
 * FOR A SCREEN READER AND THE KEYBOARD (Oct 2026):
 * - It is a dialog (`role="dialog"`, `aria-modal`), NAMED BY ITS OWN HEADING
 *   (`aria-labelledby`). ⚠️ Never `aria-label={title}`: a title may be an icon and words
 *   (HubSpotTaskSettings), and that was read out as "[object Object]".
 * - The focus is MOVED INTO it as it opens, and put back where it was as it closes.
 *   `aria-modal` tells a screen reader the page behind is out of reach, which is only true
 *   for a reader whose focus is in the dialog. Something inside that already has the focus
 *   (an `autoFocus` field) keeps it.
 * - ⚠️ There is NO focus trap (this header said there was): Tab can still leave the dialog.
 */

import React, { useEffect, useId, useRef } from "react";
import PropTypes from "prop-types";
import "./Modal.scss";

export const Modal = ({
  isOpen = false,
  onClose,
  title,
  children,
  footer,
  size = "medium",
  closeOnOverlayClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  className = "",
}) => {
  const titleId = useId();
  const dialogRef = useRef(null);

  // The focus goes into the dialog as it opens, and back where it was as it closes.
  useEffect(() => {
    if (!isOpen) return undefined;
    const before = document.activeElement;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
    return () => {
      if (before && typeof before.focus === "function" && document.contains(before)) before.focus();
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, closeOnEsc, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        className={`modal-content modal-content--${size} ${className}`}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && <h3 className="modal-title" id={titleId}>{title}</h3>}
            {showCloseButton && (
              <button
                type="button"
                className="modal-close-button"
                onClick={onClose}
                aria-label="Close modal"
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="modal-body">{children}</div>

        {/* Footer */}
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // words, or an icon and words: the dialog is named by the heading this is drawn in
  title: PropTypes.node,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  size: PropTypes.oneOf(["small", "medium", "large", "fullscreen"]),
  closeOnOverlayClick: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
};

export default Modal;
